import { motion } from "framer-motion";
import { ArrowUpRight, Gem } from "lucide-react";
import { TYPEFORM , withUTM } from "../data/landing";
const T = Object.fromEntries(Object.entries(TYPEFORM).map(([k, v]) => [k, withUTM(v, "about-egeez")]));

const BG1_CLOUDS = "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/yu91bpz7_BG1.png";
const DASHBOARD_IMG = "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/kjavseua_Dashboard22.png";
const USPTO_IMG = "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/i2c0hebs_image.png";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

function Eyebrow({ children, color = "#0041E6" }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase"
      style={{ color }}
    >
      <span className="inline-block w-6 h-px" style={{ background: color }} />
      {children}
    </div>
  );
}

const BLOCKS = [
  {
    eyebrow: "The problem",
    body:
      "Entertainment licensed characters / IPs consumer products and services is a $141B (SAM) industry still running on Excel sheets, faked sales reports, and guesswork. Licensors get underpaid. Licensees have no visibility. Fans get: 'sorry, we can't ship to your area.' Nobody fixed this — until now.\n\nLicensors have no real-time visibility into how their IP is being sold. Licensees are terrified of accidentally violating their territory agreements and getting sued. And fans — the people who actually buy this stuff — have no single place to find authentic, officially licensed merchandise. Big cluttered marketplaces are flooded with counterfeits. Licensees' eStores are buried. The industry is fragmented, opaque, and running on spreadsheets.",
  },
  {
    eyebrow: "The fix",
    body:
      "eGeez is the world's first AI-powered Licensing OS + Marketplace. A broken system that left licensors undercompensated, licensees overwhelmed, and fans underserved. We found the real pain. Then we built the solution. Thousands of licensed SKUs sit hidden in clutter, buried under counterfeits at inflated prices. eGeez AI indexes products globally and surfaces them to fans — geolocated to their territory, at the lowest prices. No more 'can't ship to your area.' No more counterfeits at checkout.\n\nOn top of that infrastructure sits our consumer marketplace — the only place in the world where every product listed is 100% verified as a licensed item, sold by the person who holds the legal right to sell it, in the territory they are licensed to sell it in. Fans discover products they have never seen before — authentic, territory-specific merchandise that is invisible on other giant messy platforms.",
  },
  {
    eyebrow: "Trust architecture",
    body:
      "Every licensee operates under an ePIN — human verification, AI validation, and domain verification — before any SKU goes live. Licensors mandate eGeez across their network, making compliance and royalty accuracy a platform standard, not a negotiation.",
  },
];

const PILLARS = [
  {
    tag: "For Fans",
    title: "Every Licensed Product. One Place.",
    body:
      "No searching. No region limits confusion. No missing drops. eGeez brings all licensed entertainment products into one global fan marketplace. More shelves than any retailer — built for fans, not systems. If it exists, you'll find it. If it's coming, you'll know first.",
    bg: "#FFD93D",
    accent: "#0041E6",
    fg: "#000000",
  },
  {
    tag: "For Licensees",
    title: "Sell Smarter. License Global Demand.",
    body:
      "Reach real fans in your licensed territories with no marketplace clutter. eGeez auto-organises your catalogue, enforces territory rules, and tracks royalties in real time. AI-indexed listings. Automated reporting. Direct fan shipping. Get discovered, get paid, get control.",
    bg: "#47FF7B",
    accent: "#0041E6",
    fg: "#000000",
  },
  {
    tag: "For Licensors",
    title: "Toast for licensing.",
    body:
      "SaaS for managing licensees, enforcing agreements per SKU, reconciling royalties live, and detecting fraud at a fraction of legacy costs.",
    bg: "#0041E6",
    accent: "#FFD93D",
    fg: "#FFFFFF",
  },
  {
    tag: "For the Industry",
    title: "eInsights. Census-level data.",
    body:
      "AI market intelligence replacing guesswork with real data. Which licensee sold most. Which toy moved fastest. In which territory. To which fan. And why.",
    bg: "#5B2EBE",
    accent: "#FFD93D",
    fg: "#FFFFFF",
  },
];

export default function AboutEgeezPage() {
  return (
    <div data-testid="about-egeez-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-20 md:pb-28 overflow-hidden bg-white">
        {/* BG1 cloud border image — full view background as user requested */}
        <img
          src={BG1_CLOUDS}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-bottom pointer-events-none select-none"
          draggable={false}
        />
        <div className="absolute inset-0 bg-white/60 pointer-events-none" aria-hidden />
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-[#0041E6]/8 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* LEFT — copy */}
            <div className="lg:col-span-7">
              <motion.div {...fadeUp}>
                <Eyebrow>About eGeez</Eyebrow>
              </motion.div>
              <motion.h1
                {...fadeUp}
                className="mt-6 font-display text-black leading-[0.88]"
                style={{ fontSize: "clamp(40px, 6.5vw, 104px)" }}
              >
                eGeez<sup className="text-[0.4em] align-super">®</sup>:<br />
                <span className="text-[#0041E6] italic">AI-Powered</span>
                <br />Licensing OS.
              </motion.h1>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-8 text-[18px] md:text-[20px] text-black/75 leading-relaxed max-w-2xl"
              >
                Finds verified licensees. Connects fans. Automates royalties. Delivers intelligence.
              </motion.p>
            </div>

            {/* RIGHT — Laptop with orange dashboard */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 lg:-mt-16 xl:-mt-24"
            >
              <div className="relative max-w-[560px] mx-auto">
                {/* Laptop body */}
                <div className="relative bg-[#1A1A1F] rounded-t-[18px] p-3 pb-4 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.4)]">
                  {/* Camera notch */}
                  <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#3a3a40] border border-black/30" />
                  {/* Screen */}
                  <div className="rounded-[6px] overflow-hidden bg-black aspect-[16/10] mt-2">
                    <img
                      src={DASHBOARD_IMG}
                      alt="eGeez dashboard"
                      className="w-full h-full object-cover object-left-top"
                      draggable={false}
                    />
                  </div>
                </div>
                {/* Laptop base / hinge */}
                <div className="relative h-[14px] bg-gradient-to-b from-[#2a2a30] via-[#1A1A1F] to-[#0a0a0d] rounded-b-[6px] mx-[-12px] shadow-md">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-1.5 bg-black/40 rounded-full" />
                </div>
                <div className="h-2 bg-gradient-to-b from-black/35 to-transparent mx-[-24px] rounded-b-[20px]" aria-hidden />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BLOCKS */}
      <section className="relative py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1100px] px-5 md:px-12 space-y-14">
          {BLOCKS.map((b, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 pb-14 border-b border-black/10 last:border-0"
            >
              <div className="md:col-span-4">
                <Eyebrow>{b.eyebrow}</Eyebrow>
              </div>
              <div className="md:col-span-8 text-[17px] md:text-[20px] text-black/80 leading-relaxed space-y-5">
                {b.body.split("\n\n").map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-24 md:py-32 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl">
            <Eyebrow>Four sides. One OS.</Eyebrow>
            <h2
              className="mt-5 font-display text-black leading-[0.92]"
              style={{ fontSize: "clamp(40px, 6vw, 84px)" }}
            >
              Built for every<br />
              stakeholder in<br />
              <span className="text-[#0041E6] italic">licensing.</span>
            </h2>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.07 }}
                className="rounded-[28px] p-8 md:p-10 min-h-[320px] flex flex-col"
                style={{ background: p.bg, color: p.fg }}
              >
                <div
                  className="inline-block w-fit text-[11px] font-bold tracking-[0.28em] uppercase px-3 py-1 rounded-full"
                  style={{ background: p.accent, color: p.bg }}
                >
                  {p.tag}
                </div>
                <h3
                  className="mt-5 font-display italic leading-[0.95]"
                  style={{ fontSize: "clamp(28px, 3vw, 40px)" }}
                >
                  {p.title}
                </h3>
                <p
                  className="mt-5 text-[15px] md:text-[16px] leading-relaxed"
                  style={{ color: p.fg, opacity: 0.92 }}
                >
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP / IP PROTECTION */}
      <section className="relative py-24 md:py-32 bg-white">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12">
          <motion.div
            {...fadeUp}
            className="rounded-[40px] p-10 md:p-16 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0041E6 0%, #001F7A 100%)" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
              <div className="lg:col-span-7 text-white">
                <Eyebrow color="#FFD93D">The missing layer</Eyebrow>
                <h3
                  className="mt-5 font-display leading-[0.95]"
                  style={{ fontSize: "clamp(34px, 5vw, 64px)" }}
                >
                  WIPO &amp; USPTO champion IP rights globally. eGeez is the{" "}
                  <span className="italic text-[#FFD93D]">commercial enforcement layer.</span>
                </h3>
                <p className="mt-6 text-[16px] md:text-[18px] text-white/85 leading-relaxed">
                  A partnership is inevitable.
                </p>
              </div>

              {/* USPTO sign — sits nicely in the empty space */}
              <motion.div
                initial={{ opacity: 0, scale: 0.92, rotate: -2 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 2 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="lg:col-span-5 flex justify-center lg:justify-end"
              >
                <div className="relative w-[260px] md:w-[320px]">
                  <div className="absolute -inset-6 bg-[#FFD93D]/20 blur-3xl rounded-full" aria-hidden />
                  <img
                    src={USPTO_IMG}
                    alt="United States Patent and Trademark Office"
                    className="relative w-full h-auto rounded-[20px] shadow-[0_30px_60px_-10px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
                    draggable={false}
                  />
                </div>
              </motion.div>
            </div>

            {/* decorative corner accents */}
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-white/5 blur-3xl pointer-events-none" aria-hidden />
            <div className="absolute -bottom-20 -left-20 w-[340px] h-[340px] rounded-full bg-[#FFD93D]/10 blur-3xl pointer-events-none" aria-hidden />
          </motion.div>
        </div>
      </section>

      {/* TRACTION */}
      <section className="relative py-24 md:py-32 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto">
            <Eyebrow>Traction</Eyebrow>
            <h2
              className="mt-5 font-display text-black leading-[0.92]"
              style={{ fontSize: "clamp(40px, 6vw, 84px)" }}
            >
              The momentum<br />
              is <span className="text-[#0041E6] italic">already here.</span>
            </h2>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { k: "100s", v: "Licensees onboarded pre-launch" },
              { k: "200K+", v: "Fans on the waitlist" },
              { k: "Grant", v: "Canadian innovation winner" },
              { k: "LES", v: "Licensing Executives Society member" },
            ].map((s, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="relative rounded-[24px] bg-white border border-black/10 p-7 md:p-8 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                {/* Gem perk icon */}
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0041E6] text-[#FFD93D] flex items-center justify-center shadow-md rotate-[8deg]">
                  <Gem size={18} strokeWidth={2.2} fill="#FFD93D" />
                </div>
                <div
                  className="font-display text-[#0041E6]"
                  style={{ fontSize: "clamp(40px, 4vw, 60px)", lineHeight: 0.95 }}
                >
                  {s.k}
                </div>
                <div className="mt-3 text-[14px] md:text-[15px] text-black/70 leading-snug">
                  {s.v}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-16 text-center"
          >
            <div className="font-display text-black leading-[0.95]" style={{ fontSize: "clamp(36px, 5vw, 72px)" }}>
              🚀 Launching <span className="text-[#0041E6] italic">2026.</span>
            </div>
            <p className="mt-4 text-[18px] md:text-[22px] text-black/70">
              The industry will not look the same.
            </p>
            <a
              href={T.joinWaitlist}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-join-waitlist-btn"
              className="mt-10 inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-semibold text-[17px] rounded-full pl-8 pr-2 h-[58px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.55)]"
            >
              Join the Waitlist
              <span className="w-[42px] h-[42px] rounded-full bg-white text-[#0041E6] flex items-center justify-center">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
